import { useMemo } from 'react';
import { useGas } from '@/hooks/queries';
import { Chart } from '@/components/Chart';
import { Avatar, CardSkeleton, SectionHead, StatLine } from '@/components/ui';
import { formatBRL, formatBRLShort, formatDate, formatNumber } from '@/lib/format';
import type { GasSummary, GasWeek, Receipt } from '@/lib/types';
import './gas.css';

interface GasCardProps {
  year: number;
  /** Filtro do administrador; o membro vê sempre o próprio gás. */
  memberId?: string;
  onSelect: (receipt: Receipt) => void;
}

/** "08/06" — a segunda-feira que abre a semana. */
const weekLabel = (weekStart: string) => `${weekStart.slice(8, 10)}/${weekStart.slice(5, 7)}`;

const plural = (count: number, one: string, many: string) => `${formatNumber(count)} ${count === 1 ? one : many}`;

/**
 * O gás dos clientes: o que eles puseram de novo na conta, lançado pelos
 * membros com comprovante. Métrica à parte — nada daqui entra no vendido, no
 * ranking, nas metas ou na comissão — até os funis serem organizados.
 */
export function GasCard({ year, memberId, onSelect }: GasCardProps) {
  const { data, isLoading, isPlaceholderData, error } = useGas({ year, memberId });

  return (
    <section className="card gas" aria-labelledby="gas-title" aria-busy={isPlaceholderData}>
      <div className="gas-head">
        <SectionHead title={<span id="gas-title">Gás dos clientes</span>}>
          <span className="chip" title="Fica fora do vendido, do ranking, das metas e da comissão">
            Fora do vendido e do salário
          </span>
        </SectionHead>
      </div>

      {error ? (
        <div className="alert alert-error" role="alert">Não deu para carregar o gás: {(error as Error).message}</div>
      ) : isLoading || !data ? (
        <CardSkeleton height={180} />
      ) : (
        <div className={`metrics-body${isPlaceholderData ? ' is-stale' : ''}`}>
          <GasBody data={data} onSelect={onSelect} />
        </div>
      )}
    </section>
  );
}

/** Semanas mínimas no gráfico, para poucas barras não virarem colunas largas demais. */
const MIN_WEEKS = 8;

/** As semanas a partir da primeira com gás — o ano inteiro vazio só espremeria as barras num canto. */
function visibleWeeks(weekly: GasWeek[]): GasWeek[] {
  const first = weekly.findIndex((week) => week.count > 0);
  if (first === -1) return weekly.slice(-MIN_WEEKS);
  return weekly.slice(Math.max(0, Math.min(first, weekly.length - MIN_WEEKS)));
}

function GasBody({ data, onSelect }: { data: GasSummary; onSelect: (receipt: Receipt) => void }) {
  const weeks = useMemo(() => visibleWeeks(data.weekly), [data.weekly]);
  const labels = useMemo(() => weeks.map((week) => weekLabel(week.weekStart)), [weeks]);
  const values = useMemo(() => weeks.map((week) => week.total), [weeks]);

  if (data.count === 0) {
    return (
      <div className="empty gas-empty">
        Nenhum gás lançado em {data.year}. Para registrar, abra o cliente em <strong>Clientes</strong> e use{' '}
        <strong>Registrar gás</strong> — ou anexe um comprovante com o tipo <strong>Gás</strong>.
      </div>
    );
  }

  return (
    <>
      <div className="metric-row gas-figures">
        <StatLine
          label={`Gás em ${data.year}`}
          value={formatBRLShort(data.total)}
          color="var(--emphasis)"
          sub={plural(data.count, 'lançamento', 'lançamentos')}
        />
        {data.thisWeek ? (
          <StatLine
            label="Esta semana"
            value={formatBRLShort(data.thisWeek.total)}
            sub={data.thisWeek.count > 0 ? `${plural(data.thisWeek.leads, 'cliente', 'clientes')} desde ${weekLabel(data.thisWeek.weekStart)}` : `nada desde ${weekLabel(data.thisWeek.weekStart)}`}
          />
        ) : null}
        <StatLine label="Clientes com gás" value={formatNumber(data.leads)} sub="pelo menos uma vez no ano" />
        <StatLine
          label="Recorrentes"
          value={formatNumber(data.recurringLeads)}
          sub="gás em 2 semanas ou mais"
        />
      </div>

      <div>
        <div className="gas-chart-head">
          <span className="section-title">Gás por semana</span>
          <span className="muted">
            segunda a domingo{weeks[0] ? `, desde ${weekLabel(weeks[0].weekStart)}` : ''}
          </span>
        </div>
        <Chart
          type="bar"
          labels={labels}
          data={values}
          label="Gás"
          height={180}
          ariaLabel={`Gráfico do gás por semana em ${data.year}`}
        />
      </div>

      <div className={`gas-lower${data.members.length > 0 ? ' is-split' : ''}`}>
        {data.members.length > 0 ? (
          <div>
            <div className="label gas-list-title">Por membro</div>
            <ul className="gas-members">
              {data.members.map((member) => (
                <li key={member.id}>
                  <Avatar name={member.name} color={member.avatarColor} photo={member.photoUrl} size={24} />
                  <span className="truncate gas-member-name">{member.name}</span>
                  <span className="muted nowrap gas-member-leads">{plural(member.leads, 'cliente', 'clientes')}</span>
                  <span className="num nowrap gas-member-total">{formatBRLShort(member.total)}</span>
                </li>
              ))}
            </ul>
          </div>
        ) : null}

        <div>
          <div className="label gas-list-title">Últimos lançamentos</div>
          <div className="gas-recent">
            {data.recent.map((receipt) => (
              <button key={receipt.id} type="button" className="receipt-line" onClick={() => onSelect(receipt)}>
                <span className="num nowrap gas-recent-date">{formatDate(receipt.date)}</span>
                <span className="truncate gas-recent-who">
                  {receipt.leadName ?? 'Sem cliente'}
                  {data.scope.memberId ? null : <span className="muted"> · {receipt.memberName ?? '—'}</span>}
                </span>
                <span className="num nowrap" style={{ fontWeight: 500 }}>{formatBRL(receipt.soldAmount)}</span>
              </button>
            ))}
          </div>
        </div>
      </div>
    </>
  );
}
